// Constrain Tab navigation to a container — used by modals (welcome-popup),
// menus (user-menu) and drawers (slide-out). Returns a destroy fn that
// restores focus to the element that had it before activation.

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export interface FocusTrap {
  destroy(): void;
}

export function activateFocusTrap(container: HTMLElement): FocusTrap {
  const active = document.activeElement;
  const previouslyFocused: HTMLElement | null =
    active instanceof HTMLElement
      ? active
      : ((active as Element | null)?.closest<HTMLElement>('button, a, [tabindex]') ?? null);

  // Container needs to be focusable for the empty-panel fallback to work.
  const hadTabindex = container.hasAttribute('tabindex');
  if (!hadTabindex) container.setAttribute('tabindex', '-1');

  const focusFirst = () => {
    const first = container.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
    (first ?? container).focus({ preventScroll: true });
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key !== 'Tab') return;
    const focusables = Array.from(
      container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
    ).filter((el) => !el.hasAttribute('disabled') && el.offsetParent !== null);
    if (focusables.length === 0) {
      e.preventDefault();
      return;
    }
    const first = focusables[0]!;
    const last = focusables[focusables.length - 1]!;
    const current = document.activeElement as HTMLElement | null;

    if (e.shiftKey && current === first) {
      e.preventDefault();
      last.focus({ preventScroll: true });
    } else if (!e.shiftKey && current === last) {
      e.preventDefault();
      first.focus({ preventScroll: true });
    }
  };

  // Catch programmatic .focus() calls from external code (chatbots, banners)
  // that would otherwise leak focus out of the trap.
  const onFocusIn = (e: FocusEvent) => {
    const target = e.target as Node | null;
    if (target && !container.contains(target)) {
      e.stopPropagation();
      focusFirst();
    }
  };

  document.addEventListener('keydown', onKeyDown);
  document.addEventListener('focusin', onFocusIn, true);

  // Run focus async so callers can finish DOM mutations before we grab focus.
  queueMicrotask(focusFirst);

  return {
    destroy: () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('focusin', onFocusIn, true);
      if (!hadTabindex) container.removeAttribute('tabindex');
      if (previouslyFocused && document.body.contains(previouslyFocused)) {
        previouslyFocused.focus({ preventScroll: true });
      }
    },
  };
}
